<template>
    <div v-if="products" ref="sliderElement">
        <SliderBase
            :slides-per-view="slidesPerView ?? {
                xs: isOffcanvas ? 2.5 : 2.3,
                sm: isOffcanvas ? 4.5 : products.length >= parseInt(productQty.value) ? 3 : products.length,
                md: isOffcanvas ? 2.5 : products.length >= parseInt(productQty.value) ? 4 : products.length,
                lg: isOffcanvas ? 2.5 : parseInt(productQty.value)
            }"
            :slide-count="products.length"
            :mobile-grid="false"
            :display-nav="displayNav"
            :space-between="spaceBetween"
            :progress-config="props?.progressBar ? 'progress_bar' : 'disabled'"
            :mobile-nav-only="mobileNavOnly"
            :nav-class="navClass"
            @update:teaser-height="setTeaserWrapperHeight"
        >
            <template #default="{ index }">
                <div ref="slides" class="relative product-swiper-slide">
                    <ProductListingCard
                        v-if="products[index]?.type === 'product'"
                        :key="products[index].data.id"
                        :data="products[index].data"
                        :is-offcanvas="isOffcanvas"
                        :index="index"
                        :variant="variant"
                        @click="onListingCardClick(products[index].data, index, $event)"
                    />
                    <ProductListingTeaser
                        v-if="products[index]?.type === 'teaser'"
                        :data="products[index].data"
                        :use-swiper-height="teaserWrapperHeight"
                        :index="index"
                    />
                </div>
            </template>
        </SliderBase>
    </div>
</template>

<script setup lang="ts">
import type { Schemas } from '#shopware'
import type { ListingItemProduct, ListingItemTeaser } from '~/types/ListingItems'

type SliderItem<T> = T extends { type: 'product' } ? ListingItemProduct : ListingItemTeaser

interface ProductSliderProps {
    products: SliderItem<ListingItemProduct | ListingItemTeaser>[]
    productQty?: { value: string, source: string }
    isOffcanvas?: boolean
    displayNav?: boolean
    progressBar?: boolean
    sliderId?: string
    sliderTitle?: string
    itemListId?: string
    itemListName?: string
    /** Display variant for product cards: 'full' shows all info, 'image-only' shows only images */
    variant?: 'full' | 'image-only'
    /** Custom slides per view config for each breakpoint. If not provided, uses default logic based on productQty */
    slidesPerView?: {
        xs?: number
        sm?: number
        md?: number
        lg?: number,
        xl?: number
    }
    /** Show navigation controls only on mobile (below sm breakpoint) */
    mobileNavOnly?: boolean
    /** Additional classes to apply to navigation controls */
    navClass?: string
    /** Gap between slides in px */
    spaceBetween?: number
}

const props = withDefaults(defineProps<ProductSliderProps>(),{
    productQty: () => ({ value: '6', source: 'static' }),
    isOffcanvas: false,
    displayNav: true,
    sliderId: 'product_slider',
    sliderTitle: 'Product Slider',
    itemListId: 'product_slider',
    itemListName: 'Product Slider',
    variant: 'full',
    spaceBetween: 8
})

const emits = defineEmits(['on-listing-card-click'])

const slides = ref([])
const nuxtApp = useNuxtApp()
const teaserWrapperHeight = ref<number | undefined>(undefined)

// Add ref for intersection observer
const sliderElement = ref<HTMLElement | null>(null)

// Compute final values with fallback
const finalItemListId = computed(() => props.itemListId || props.sliderId || 'product-slider')
const finalItemListName = computed(() => props.itemListName || props.sliderTitle || 'Product slider')

// Extract actual product objects for tracking
const productObjects = computed(() => {
    return props.products
        .filter(item => item.type === 'product')
        .map(item => item.data)
})

// Use item list observer for view_item_list
useItemListObserver(
    sliderElement,
    finalItemListId.value,
    finalItemListName.value,
    productObjects.value
)

function onListingCardClick (product: Schemas['Product'], index: number, event: Event) {
    emits('on-listing-card-click', { product, index, event })
    const itemListContextStore = useItemListContextStore()

    // Store list context
    itemListContextStore.setItemListContextData({
        item_list_id: finalItemListId.value,
        item_list_name: finalItemListName.value,
        index
    });

    // Emit tracking event with final computed values
    nuxtApp.callHook('clickProductListingCard', {
        id: finalItemListId.value,
        name: finalItemListName.value,
        product,
        index
    })
}

function setTeaserWrapperHeight (height : number) {
    teaserWrapperHeight.value = height
}

// Set up keyboard navigation
const { updateFocusableElements } = useKeyboardNavigation(slides, {
    useFirstChildOnly: true, // Only focus the first focusable element in each slide
    childSelector: '.product-swiper-slide', // Target slides
    selector: '[tabindex]:not([tabindex="-1"])'
})

watch(() => props.products, () => {
    nextTick(() => {
        updateFocusableElements()
    })
}, { deep: true })
</script>
